import { useOktaAuth } from '@okta/okta-react';
import { Button, ButtonSizes, ButtonVariants } from '@triumph/design-system';
import { isAxiosError } from 'axios';
import { useState } from 'react';

import { ProductNames } from '@/components/providers/ApplicantContextProvider/ApplicantContextProvider.types';
import { useAddAssignee, useDeleteAssignee } from '@/components/shared/api/mutations/assignees';
import { getApplicantAssignees } from '@/components/shared/api/endpoints/applicantAssignees';
import { Feature, PERMISSION_DENIED_TITLE } from '@/components/shared/constants/features';
import { usePermissions } from '@/components/providers/PermissionsContextProvider';
import { useCurrentUserEmail } from '@/components/hooks/useCurrentUserEmail';
import {
    EVENT_LOG_NAMES,
    getLoggingContext,
    honeybadgerLogEvent,
} from '@/components/shared/utils/logging';

import {
    AssignButtonGroup,
    AssignedButton,
    ConflictError,
    UnassignButton,
} from './AssignCell.StyledComponents';

interface AssignCellProps {
    applicantId: string;
    product: ProductNames;
    currentAssigneeEmail?: string;
    currentAssigneeId?: string;
    onSuccess?: () => void;
    onConflict?: () => void;
}

export const AssignCell = ({
    applicantId,
    product,
    currentAssigneeEmail,
    currentAssigneeId,
    onConflict,
    onSuccess,
}: AssignCellProps) => {
    const { authState } = useOktaAuth();
    const { hasFeature } = usePermissions();
    const addAssigneeMutation = useAddAssignee();
    const deleteAssigneeMutation = useDeleteAssignee();

    const [conflictError, setConflictError] = useState<string | null>(null);

    const currentUserEmail = useCurrentUserEmail();
    const currentUserName = authState?.idToken?.claims?.name as string | undefined;

    const isAssignedToMe =
        !!currentAssigneeEmail &&
        currentAssigneeEmail.toLowerCase() === currentUserEmail?.toLowerCase();

    const handleAssignClick = async () => {
        if (!currentUserEmail) return;
        setConflictError(null);

        const userId = authState?.idToken?.claims?.sub;

        const eventName =
            product === ProductNames.FACTORING
                ? EVENT_LOG_NAMES.FactoringAssign
                : product === ProductNames.TCSFUEL
                  ? EVENT_LOG_NAMES.TcsFuelAssign
                  : EVENT_LOG_NAMES.LoadPayAssign;

        honeybadgerLogEvent(eventName, {
            level: 'INFO',
            customAttributes: {
                ...getLoggingContext(currentUserEmail, userId),
                currentPage: window.location.pathname,
                timestamp: new Date().toISOString(),
                applicantId,
                product,
                assigneeEmail: currentUserEmail,
            },
        });

        // Guard: fetch fresh assignees to check for conflicts before mutating
        try {
            const freshAssignees = await getApplicantAssignees(applicantId);
            const existingAssignment = freshAssignees.find(
                (a) => String(a.product).toUpperCase() === product.toUpperCase(),
            );
            if (
                existingAssignment &&
                existingAssignment.email?.toLowerCase() !== currentUserEmail.toLowerCase()
            ) {
                setConflictError(`Already assigned to ${existingAssignment.email}. Refreshing...`);
                onConflict?.();
                return;
            }
        } catch {
            // fail-open: if the pre-check errors, let the mutation proceed normally
        }

        try {
            await addAssigneeMutation.mutateAsync({
                applicantId,
                email: currentUserEmail,
                product,
            });
            onSuccess?.();
        } catch (error) {
            // A 400 from the server means the assignment was rejected — most likely a race
            // condition where another reviewer assigned between our pre-check and this call.
            // Treat it the same as a pre-check conflict: show an error and refresh assignees.
            if (isAxiosError(error) && error.response?.status === 400) {
                setConflictError('Assignment failed — may have just been taken. Refreshing...');
                onConflict?.();
            } else {
                console.error('Failed to assign:', error);
            }
        }
    };

    const handleUnassignClick = async () => {
        if (!currentAssigneeId) return;
        try {
            await deleteAssigneeMutation.mutateAsync({
                applicantId,
                assigneeId: currentAssigneeId,
            });
            onSuccess?.();
        } catch (error) {
            console.error('Failed to unassign:', error);
        }
    };

    if (currentAssigneeEmail) {
        return (
            <div>
                <AssignButtonGroup>
                    <AssignedButton
                        variant={ButtonVariants.secondary}
                        size={ButtonSizes.small}
                        data-testid="assigned-button"
                        disabled={true}
                    >
                        {isAssignedToMe
                            ? `👤 Assigned: ${currentUserName || currentUserEmail?.split('@')[0]}`
                            : `👤 Assigned: ${currentAssigneeEmail}`}
                    </AssignedButton>
                    {currentAssigneeId && (
                        <UnassignButton
                            variant={ButtonVariants.secondary}
                            size={ButtonSizes.small}
                            onClick={handleUnassignClick}
                            data-testid="unassign-button"
                            disabled={
                                deleteAssigneeMutation.isPending || !hasFeature(Feature.Assign)
                            }
                            loading={deleteAssigneeMutation.isPending || undefined}
                            title={
                                !hasFeature(Feature.Assign) ? PERMISSION_DENIED_TITLE : undefined
                            }
                        >
                            ✘
                        </UnassignButton>
                    )}
                </AssignButtonGroup>
                {conflictError && (
                    <ConflictError data-testid="assign-conflict-error">
                        {conflictError}
                    </ConflictError>
                )}
            </div>
        );
    }

    return (
        <div>
            <Button
                variant={ButtonVariants.secondary}
                size={ButtonSizes.small}
                onClick={handleAssignClick}
                data-testid="assign-button"
                disabled={
                    addAssigneeMutation.isPending || !currentUserEmail || !hasFeature(Feature.Assign)
                }
                loading={addAssigneeMutation.isPending || undefined}
                title={!hasFeature(Feature.Assign) ? PERMISSION_DENIED_TITLE : undefined}
            >
                👤 Assign to Me
            </Button>
            {conflictError && (
                <ConflictError data-testid="assign-conflict-error">{conflictError}</ConflictError>
            )}
        </div>
    );
};
